import React, { useState } from "react";
import { useSignUp } from "@clerk/clerk-react";
import { Link, useNavigate, useSearchParams } from "@/src/lib/next-compat";
import {
  validateName,
  validateUsername,
  validateEmail,
  validatePassword,
} from "@/lib/validation";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";

export default function SignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const rawRedirectUrl = searchParams.get("redirect_url");
  const storedPendingJoin = typeof window !== "undefined" ? sessionStorage.getItem("inara_pending_join") : null;
  const redirectUrl = rawRedirectUrl || storedPendingJoin || "";

  const handleSignUpRedirect = () => {
    let target = redirectUrl || "/courses";
    if (target.startsWith("/join/")) {
      const sep = target.includes("?") ? "&" : "?";
      if (!target.includes("auto_join=true")) {
        target = `${target}${sep}auto_join=true`;
      }
    }
    navigate(target);
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({
    firstName: false,
    lastName: false,
    username: false,
    email: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Email verification state
  const [verifying, setVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // Compute real-time validation for each field
  const firstNameValidation = validateName(formData.firstName, "First name", touched.firstName);
  const lastNameValidation = validateName(formData.lastName, "Last name", touched.lastName);
  const usernameValidation = validateUsername(formData.username, touched.username);
  const emailValidation = validateEmail(formData.email, touched.email);
  const passwordValidation = validatePassword(formData.password, touched.password);

  // Strict check on all fields (whether touched or not) for enabling submission
  const isAllValid =
    validateName(formData.firstName, "First name", true).isValid &&
    validateName(formData.lastName, "Last name", true).isValid &&
    validateUsername(formData.username, true).isValid &&
    validateEmail(formData.email, true).isValid &&
    validatePassword(formData.password, true).isValid;

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (apiError) setApiError(null);
  };

  const handleBlur = (field: keyof typeof formData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    // Mark all as touched to trigger any empty field warnings
    setTouched({
      firstName: true,
      lastName: true,
      username: true,
      email: true,
      password: true,
    });

    if (!isAllValid) {
      setApiError("Please fix all validation errors before proceeding.");
      return;
    }

    if (!isLoaded || !signUp) {
      setApiError("Authentication service is initializing. Please try again.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signUp.create({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        username: formData.username.trim(),
        emailAddress: formData.email.trim(),
        password: formData.password,
      });

      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        handleSignUpRedirect();
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setVerifying(true);
      }
    } catch (err: any) {
      const clerkErrors = err?.errors;
      if (Array.isArray(clerkErrors) && clerkErrors.length > 0) {
        setApiError(clerkErrors[0].longMessage || clerkErrors[0].message || "Sign up failed.");
      } else {
        setApiError(err?.message || "Failed to create account. Please check your information.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setVerificationError("Please enter the verification code.");
      return;
    }

    if (!isLoaded || !signUp) return;

    setSubmitting(true);
    setVerificationError(null);
    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code: verificationCode.trim(),
      });

      if (completeSignUp.status === "complete") {
        await setActive({ session: completeSignUp.createdSessionId });
        handleSignUpRedirect();
      } else {
        setVerificationError("Verification incomplete. Please check your code.");
      }
    } catch (err: any) {
      const clerkErrors = err?.errors;
      if (Array.isArray(clerkErrors) && clerkErrors.length > 0) {
        setVerificationError(clerkErrors[0].longMessage || clerkErrors[0].message || "Invalid verification code.");
      } else {
        setVerificationError(err?.message || "Invalid verification code.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded || !signUp) return;
    setResending(true);
    setVerificationError(null);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
    } catch {
      setVerificationError("Failed to resend code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full">
      {/* Left Side - Branding & Testimonial */}
      <div className="hidden lg:flex w-1/2 bg-blue-600 flex-col justify-between p-12 text-white relative overflow-hidden">
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img
              src="/images/logo-v2.png"
              alt="Inara X Logo"
              width={120}
              height={40}
              className="h-8 w-auto brightness-0 invert"
            />
          </Link>

          <h1 className="text-4xl font-bold mb-6 leading-tight">
            Start your personalized<br />learning adventure
          </h1>
          <p className="text-blue-100 text-lg max-w-md">
            Join thousands of students who are mastering new skills faster with Inara&apos;s AI-driven curriculum.
          </p>
        </div>

        {/* Decorative Circles */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-blue-500 opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-blue-400 opacity-20 blur-3xl"></div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-slate-50 overflow-y-auto">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-slate-200 shadow-xl">
          <div className="lg:hidden mb-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <img
                src="/images/logo-v2.png"
                alt="Inara X Logo"
                width={120}
                height={40}
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {!verifying ? (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Create your account</h2>
                <p className="text-sm text-slate-500 mt-1">
                  All fields are required.
                </p>
              </div>

              {apiError && (
                <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <span>{apiError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John"
                      required
                      aria-required="true"
                      value={formData.firstName}
                      onChange={(e) => handleFieldChange("firstName", e.target.value)}
                      onBlur={() => handleBlur("firstName")}
                      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-900 placeholder-slate-400 outline-none transition ${
                        touched.firstName && !firstNameValidation.isValid
                          ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                          : touched.firstName && firstNameValidation.isValid && formData.firstName
                          ? "border-emerald-400 bg-emerald-50/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                          : "border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      }`}
                    />
                    {touched.firstName && !firstNameValidation.isValid && (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {firstNameValidation.error}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Doe"
                      required
                      aria-required="true"
                      value={formData.lastName}
                      onChange={(e) => handleFieldChange("lastName", e.target.value)}
                      onBlur={() => handleBlur("lastName")}
                      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-900 placeholder-slate-400 outline-none transition ${
                        touched.lastName && !lastNameValidation.isValid
                          ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                          : touched.lastName && lastNameValidation.isValid && formData.lastName
                          ? "border-emerald-400 bg-emerald-50/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                          : "border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      }`}
                    />
                    {touched.lastName && !lastNameValidation.isValid && (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {lastNameValidation.error}
                      </p>
                    )}
                  </div>
                </div>

                {/* Username */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. johndoe12"
                      required
                      aria-required="true"
                    value={formData.username}
                    onChange={(e) => handleFieldChange("username", e.target.value)}
                    onBlur={() => handleBlur("username")}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-900 placeholder-slate-400 outline-none transition ${
                      touched.username && !usernameValidation.isValid
                        ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                        : touched.username && usernameValidation.isValid && formData.username
                        ? "border-emerald-400 bg-emerald-50/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        : "border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    }`}
                  />
                  {touched.username && !usernameValidation.isValid ? (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {usernameValidation.error}
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-400 mt-1">Letters and numbers only. No spaces or symbols.</p>
                  )}
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="john@example.com"
                    autoComplete="email"
                    required
                    aria-required="true"
                    value={formData.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-slate-900 placeholder-slate-400 outline-none transition ${
                      touched.email && !emailValidation.isValid
                        ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                        : touched.email && emailValidation.isValid && formData.email
                        ? "border-emerald-400 bg-emerald-50/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        : "border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    }`}
                  />
                  {touched.email && !emailValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {emailValidation.error}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      required
                      aria-required="true"
                      value={formData.password}
                      onChange={(e) => handleFieldChange("password", e.target.value)}
                      onBlur={() => handleBlur("password")}
                      className={`w-full px-3.5 py-2.5 pr-10 rounded-xl border text-sm text-slate-900 placeholder-slate-400 outline-none transition ${
                        touched.password && !passwordValidation.isValid
                          ? "border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                          : touched.password && passwordValidation.isValid && formData.password
                          ? "border-emerald-400 bg-emerald-50/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                          : "border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {touched.password && !passwordValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {passwordValidation.error}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || !isAllValid}
                  className="w-full mt-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl shadow-md transition active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center text-xs text-slate-500">
                Already have an account?{" "}
                <Link
                  to={`/sign-in${redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : ""}`}
                  className="font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Sign in
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Verify your email</h2>
                <p className="text-xs text-slate-500 mt-1.5">
                  We sent a 6-digit verification code to <span className="font-semibold text-slate-800">{formData.email}</span>.
                </p>
              </div>

              {verificationError && (
                <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <span>{verificationError}</span>
                </div>
              )}

              <form onSubmit={handleVerifyCode} className="space-y-4" noValidate>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    required
                    aria-required="true"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full px-4 py-3 text-center tracking-widest font-mono text-lg rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || verificationCode.length < 6}
                  className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-sm rounded-xl shadow-md transition flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Complete Registration"
                  )}
                </button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resending}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold disabled:opacity-50"
                  >
                    {resending ? "Sending code..." : "Didn't receive a code? Resend"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

